import { Box, Typography } from '@mui/material';
import { JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useHorizontalOverflow } from '../../library/useHorizontalOverflow';
import ResourcesIds from '../../resources/ResourcesIds';
import { scrollHintStyles, scrollTrackStyles, scrollViewportStyles } from './chartScroller.styles';

/** Roughly what one month's group needs: the gap between groups, plus a bar per series. */
const MONTH_GAP_PX = 28;
const PER_SERIES_PX = 18;
const MONTHS = 12;

/**
 * How wide the chart has to be before its bars and their labels stop overlapping.
 *
 * Self-regulating, which is why there is no breakpoint in it: a clinic with two therapists
 * needs about 980px and so never scrolls on a desktop, the same chart on a phone always
 * does, and a nine-series roster scrolls everywhere. Exported for the tests.
 */
export const trackWidthFor = (visibleSeries: number): number =>
    MONTHS * (MONTH_GAP_PX + PER_SERIES_PX * Math.max(1, visibleSeries));

interface ChartScrollerProps {
    visibleSeries: number;
    /** Names the scrollable region for a screen reader. */
    label: string;
    children: ReactNode;
}

/**
 * Lets a chart be wider than the card holding it.
 *
 * Twelve months of grouped bars with a number printed above each one needs more horizontal
 * room than a phone has, and squeezing them in produces bars a few pixels wide with their
 * labels on top of one another. Scrolling trades a swipe for legibility, and keeps the
 * promise that the *page* never scrolls sideways -- the overflow lives here, inside a card
 * that is already width-constrained.
 *
 * The viewport is a focusable region on purpose: a scrollable area with nothing focusable
 * inside it cannot be reached or driven by keyboard at all, so December would be
 * unreachable without a mouse.
 *
 * The hint is shown from a measurement rather than a breakpoint, because the width that
 * decides it belongs to the roster, not to the screen: nine therapists scroll on a desktop
 * and two do not scroll on a tablet. Toggling a series off in the legend narrows the track
 * and can retire the hint mid-session, which is why it is observed rather than read once.
 */
export const ChartScroller = ({ visibleSeries, label, children }: ChartScrollerProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const [viewportRef, isScrollable] = useHorizontalOverflow<HTMLDivElement>();

    return (
        <>
            <Box ref={viewportRef} sx={scrollViewportStyles} role="region" tabIndex={0} aria-label={label}>
                <Box sx={scrollTrackStyles(trackWidthFor(visibleSeries))}>{children}</Box>
            </Box>

            {isScrollable && (
                <Typography variant="caption" sx={scrollHintStyles}>
                    {translate(ResourcesIds.DASHBOARD_CHART_SCROLL_HINT)}
                </Typography>
            )}
        </>
    );
};
